// ============================================================================
// SCOPE CHIP
// ============================================================================
// Inline badge that tells the user WHERE a config value came from — powers
// the "Inherited from ENTITY" / "Overridden here: BUSINESS_UNIT" hints on
// the Settings screen and any domain screen that surfaces config-driven
// fields (e.g. Voucher Type form showing where PREFIX resolves from).
//
// Two modes:
//   inherited (grey)  — value came from a scope ABOVE the currently-viewed one
//   overridden (blue) — value was authored AT the currently-viewed scope
//
// Purely presentational — the caller decides which mode to render based on
// comparing `resolvedFromScopeType` to the scope the user is currently editing.
// ============================================================================

import React from 'react';
import type { ConfigScopeType } from 'api/types/config.types';

export interface ScopeChipProps {
  /** Which scope actually produced this value (from ResolvedConfigDto). */
  resolvedFromScopeType?: ConfigScopeType | string;
  /** Optional display name of the resolved scope ("Bangalore Branch", "FY 2025-26"). */
  resolvedFromScopeName?: string;
  /**
   * The scope the user is currently editing — decides whether the chip
   * reads "Inherited from…" (came from higher) or "Overridden here" (this
   * scope). Omit for a pure "Inherited from…" chip.
   */
  currentScopeType?: ConfigScopeType | string;
  /** Backend flagged the value came from definition.default_value, not any scope. */
  fromDefault?: boolean;
  /** Extra CSS class hook. */
  className?: string;
  /** Compact 10px variant for dense form contexts. */
  compact?: boolean;
  /** Click handler — typically opens the Settings screen at the resolved scope. */
  onClick?: () => void;
}

// FBP DISPLAY-ONLY labels for scope-type enum values. The framework's
// scope_type enum stays 'BUSINESS_UNIT' at the API / DB / TypeScript level so
// other adopting apps (CRS "Property", warehouse app "Warehouse") can
// override the label without touching the framework. Here we override
// BUSINESS_UNIT → "Branch" to match the term the main-layout header uses.
const SCOPE_LABELS: Record<string, string> = {
  SYSTEM: 'System default',
  ENTERPRISE: 'Enterprise',
  ENTITY: 'Entity',
  BUSINESS_UNIT: 'Branch',
  FINANCIAL_YEAR: 'Financial Year',
  VOUCHER_TYPE: 'Voucher Type',
};

function humanizeScope(scope?: string): string {
  if (!scope) return 'Unknown';
  return SCOPE_LABELS[scope] ?? scope;
}

const ScopeChip: React.FC<ScopeChipProps> = ({
  resolvedFromScopeType,
  resolvedFromScopeName,
  currentScopeType,
  fromDefault,
  className,
  compact,
  onClick,
}) => {
  // Handle fully-unresolved case (backend returned no value at all).
  if (!resolvedFromScopeType && !fromDefault) {
    return null;
  }

  const isOverriddenHere =
    currentScopeType != null && resolvedFromScopeType === currentScopeType;

  const isDefault = fromDefault === true;

  const label = isDefault
    ? 'Framework default'
    : isOverriddenHere
      ? `Overridden here: ${humanizeScope(resolvedFromScopeType)}`
      : `Inherited from ${humanizeScope(resolvedFromScopeType)}`;

  const suffix = resolvedFromScopeName ? ` — ${resolvedFromScopeName}` : '';

  // Colour palette matches the FBP form conventions from CLAUDE.md:
  //   grey  → inherited / default (passive information)
  //   blue  → overridden here (actionable — user changed this value at this level)
  const styles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: compact ? '0.6875rem' : '0.75rem',
    fontWeight: 500,
    padding: compact ? '0.125rem 0.375rem' : '0.1875rem 0.5rem',
    borderRadius: '0.25rem',
    background: isOverriddenHere
      ? 'var(--scope-chip-override-bg, #dbeafe)'
      : 'var(--scope-chip-inherit-bg, #f1f5f9)',
    color: isOverriddenHere
      ? 'var(--scope-chip-override-fg, #1e40af)'
      : 'var(--scope-chip-inherit-fg, #475569)',
    border: '1px solid',
    borderColor: isOverriddenHere
      ? 'var(--scope-chip-override-border, #93c5fd)'
      : 'var(--scope-chip-inherit-border, #cbd5e1)',
    cursor: onClick ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  };

  return (
    <span
      className={`scope-chip${className ? ` ${className}` : ''}`}
      style={styles}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={`${label}${suffix}`}
    >
      {label}
      {suffix}
    </span>
  );
};

export default ScopeChip;
